import { useAuth0 } from '@auth0/auth0-react';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Tab, TabList, TabPanel, useTabState } from 'reakit/Tab';
import { wrapperDiv } from '../../constants/app';
import {
  ErrorResponse,
  WeSecureWifiCredentialsInterface,
} from '../../api/wifi/client';
import { Header } from './components/Header';
import { Nav } from './components/Nav';
import { DeviceTab } from './components/Tabs/DeviceTab';
import { PersonalTab } from './components/Tabs/PersonalTab';
import styles from './WifiConnect.module.scss';
import { Footer } from './components/Footer';
import { isSupported } from '../../utils/utils';
import { template } from '../../app/unsupported-browser';
import Loader from 'react-loader-spinner';
import { FormattedMessage } from 'react-intl';
import { WarningMessage } from './components/WarningMessage';
import { CorporateTab } from './components/Tabs/CorporateTab';
import { CompanyDevicesTab } from './components/Tabs/CompanyDevicesTab';
import { DeviceResponse } from '../../api/devices/types';
import { getDeviceClient } from '../../api/devices/client';
import { AppConfig } from '../../constants/config';

const needsRTL = () => {
  const country = navigator.language.substr(0, 2);
  return country === 'ar' ? 'rtl' : 'ltr';
};

const userIsInactiveMessage = 'user_is_inactive';

const WifiConnect: React.FC = () => {
  const tab = useTabState({ selectedId: 'weworkwifi' });
  const { logout } = useAuth0();

  // we need this changes for a sending 'locations' and 'devices' tab only when we click on the device tab
  const [sendDeviceReqDetector, setSendDeviceReqDetector] = useState(false);
  const [devices, setDevices] = useState<DeviceResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [clicked, setClicked] = useState(false);

  const handleClickSendDeviceReq = () => {
    if (!clicked) {
      setLoading(true);
      loadDevices();
      setClicked(true);
    }
  };

  const [sendCorporateReqDetector, setCorporateReqDetector] = useState(false);
  const [isCorporateReqWasSent, setIsCorporateWasSent] = useState(false);
  const [
    isCompanyDeviceTabWasClicked,
    setIsCompanyDeviceTabWasClicked,
  ] = useState(false);
  const [
    companyDevicesTabClickDetector,
    setCompanyDevicesTabClickDetector,
  ] = useState(false);
  const handleClickSendCorporateReq = () => {
    if (!isCorporateReqWasSent) {
      setCorporateReqDetector((oldState) => !oldState);
      setIsCorporateWasSent(true);
    }
  };
  const handleClickCorporateDeviceTab = () => {
    if (!isCompanyDeviceTabWasClicked) {
      setCompanyDevicesTabClickDetector((oldState) => !oldState);
      setIsCompanyDeviceTabWasClicked(true);
    }
  };

  const { getAccessTokenSilently } = useAuth0();

  const loadDevices = async () => {
    const token = await getAccessTokenSilently();
    const pageSize = 100;

    let devices = [];
    let isStart = true;
    let newDevices: DeviceResponse[] = [];
    let nextOffset = 0;

    while (isStart || newDevices.length === pageSize) {
      const resp = await getDeviceClient(
        AppConfig.appEnv,
        token,
      ).getListOfDevices(pageSize, nextOffset);

      if ('devices' in resp) {
        devices.push(...resp.devices);
        newDevices = resp.devices;
        nextOffset = resp.next_offset;
        isStart = false;
      } else {
        isStart = false;
        break;
      }
    }

    setDevices(devices);
    setLoading(false);
  };

  const [credentials, setCredentials] = useState<
    WeSecureWifiCredentialsInterface
  >({
    username: '',
    password: '',
    network_name: '',
    device_network_name: '',
    shared_key: '',
    has_corporate_tab_access: false,
    has_corp_devices_access: false,
    has_team_devices_access: false,
    company_data: [],
    show_device_tab: false,
  });

  const [errorMsg, setErrorMsg] = useState<ErrorResponse>();
  return (
    <main
      dir={needsRTL()}
      className={classNames('ray-page-container', styles.wifiConnectContainer)}
    >
      <WarningMessage />
      <div className="ray-grid">
        <Nav onLogout={logout} />

        {isSupported() ? (
          <>
            {errorMsg ? (
              errorMsg.status === userIsInactiveMessage ? (
                <div className={classNames('ray-grid__cell--span-full')}>
                  <div className={classNames(wrapperDiv, styles.centered)}>
                    <div className={styles.errorPermissions}>
                      <h1 className="ray-text--h1">
                        <FormattedMessage
                          id="header.userInactivityMessage.title"
                          defaultMessage="Access denied"
                        />
                      </h1>
                      <span className="ray-text--body-large">
                        <FormattedMessage
                          id="header.userInactivityMessage.description"
                          defaultMessage="Your membership with WeWork is no longer active. For assistance, please reach out to {email}."
                          values={{
                            email: (
                              <a href="mailto:help@wework.com">
                                help@wework.com
                              </a>
                            ),
                          }}
                        />
                      </span>
                      <br />

                      <button
                        className={styles.logoutLink}
                        onClick={() => logout()}
                      >
                        <FormattedMessage
                          id="nav.logout"
                          defaultMessage="Log out"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Header error_msg={errorMsg} />
              )
            ) : (
              <>
                <Header />
                <div className={classNames('ray-grid__cell--span-full')}>
                  <div className={classNames(wrapperDiv, styles.centered)}>
                    {credentials.username ? (
                      <TabList
                        {...tab}
                        aria-label="Instructions Tabs"
                        className={classNames(
                          'ray-tabs ray-grid__cell--span-full mainTabs',
                          (credentials.has_corporate_tab_access ||
                            credentials.has_team_devices_access) &&
                            credentials.show_device_tab
                            ? styles.corpTabList
                            : styles.tabList,
                        )}
                      >
                        <Tab
                          {...tab}
                          id="weworkwifi"
                          className="ray-tabs__item"
                        >
                          <span translate="no" className="notranslate">
                            {credentials.network_name}
                          </span>
                        </Tab>
                        {credentials.show_device_tab ? (
                          <Tab
                            onClick={handleClickSendDeviceReq}
                            {...tab}
                            id="device"
                            className="ray-tabs__item"
                          >
                            <span translate="no" className="notranslate">
                              {credentials.device_network_name}
                            </span>
                          </Tab>
                        ) : (
                          <></>
                        )}
                        {credentials.has_corporate_tab_access ? (
                          <Tab
                            onClick={handleClickSendCorporateReq}
                            {...tab}
                            id="corporate"
                            className="ray-tabs__item"
                          >
                            <span translate="no" className="notranslate">
                              {'Corporate'}
                            </span>
                          </Tab>
                        ) : (
                          <></>
                        )}
                        {credentials.has_team_devices_access &&
                        credentials.show_device_tab ? (
                          <Tab
                            onClick={handleClickCorporateDeviceTab}
                            {...tab}
                            id="corporate_devices"
                            className="ray-tabs__item"
                          >
                            <span translate="no" className="notranslate">
                              {'Company Devices'}
                            </span>
                          </Tab>
                        ) : (
                          <></>
                        )}
                      </TabList>
                    ) : (
                      <div className={styles.formLoader}>
                        <Loader
                          type="Oval"
                          color="#00F"
                          height={40}
                          width={40}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={classNames(
                    'ray-grid__cell--span-full',
                    styles.tabPanels,
                  )}
                >
                  <div>
                    <TabPanel {...tab} id="weworkwifi">
                      <PersonalTab
                        onAuth={setCredentials}
                        onError={setErrorMsg}
                      />
                    </TabPanel>

                    {credentials.show_device_tab ? (
                      <TabPanel {...tab} id="device">
                        <DeviceTab
                          credentials={credentials}
                          sendDeviceReqDetector={sendDeviceReqDetector}
                          devices={devices}
                          loadDevices={loadDevices}
                          isDevicesLoading={loading}
                        />
                      </TabPanel>
                    ) : (
                      <></>
                    )}
                    {credentials.has_corporate_tab_access ? (
                      <TabPanel {...tab} id="corporate">
                        <CorporateTab
                          credentials={credentials}
                          sendCorporateReqDetector={sendCorporateReqDetector}
                        />
                      </TabPanel>
                    ) : (
                      <></>
                    )}
                    {credentials.has_team_devices_access &&
                    credentials.show_device_tab ? (
                      <TabPanel {...tab} id="company_devices">
                        <CompanyDevicesTab
                          credentials={credentials}
                          onSuccess={() => {}}
                          companyDevicesTabClickDetector={
                            companyDevicesTabClickDetector
                          }
                          loadDevices={loadDevices}
                          device_network_name={credentials.device_network_name}
                        />
                      </TabPanel>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className={classNames('ray-grid__cell--span-full')}>
            <div
              className="ray-grid__cell--span-4 ray-grid__cell--push-4-desktop ray-grid__cell--push-1-tablet ray-grid__cell--span-6-tablet ray-grid__cell--span-12-phone"
              dangerouslySetInnerHTML={{ __html: template }}
            ></div>
          </div>
        )}
      </div>

      <Footer />
    </main>
  );
};

export default WifiConnect;
